import React, { createContext, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { IFormProps } from './interfaces';
import { Form as IForm } from './interfaces/form';
import Tabs from './components/Tabs';
import { FormMapper } from '@mappers/FormMapper';
import { IFormContext } from './interfaces/IFormContext';
import { FormControlType, ParticipationTypeMasktoType } from './interfaces/enums';
import { executeFunctionByName, isPromise, sanitizeGuid, formatXrmGuid, isLookupSoft, replaceLast } from '@app/Functions';
import { EntityDefinition } from '@definitions/EntityDefinition';
import { EntityDefinition as IEntityDefinition } from '@app/interfaces/entitydefinition';
import { IChildControl } from "./interfaces/IChildControl";
import { IAttributeConfiguration } from './interfaces/IAttributeConfiguration';
import { getRequiredLevel } from '@ComponentFramework/PropertyClasses/Context';
import { Text } from '@fluentui/react/lib/Text';
import './css/styles.css';
import { Card } from '@uifabric/react-cards';
import { Cell } from './components/Cell';
import { Placeholder } from './components/Placeholder';
import { useResizeDetector } from 'react-resize-detector';
import { DialogFooter } from '@fluentui/react/lib/components/Dialog/DialogFooter';
import { DefaultButton } from '@fluentui/react/lib/components/Button/DefaultButton/DefaultButton';
import { PrimaryButton } from '@fluentui/react/lib/components/Button/PrimaryButton/PrimaryButton';
import { ICommandBarItemProps, ICommandBarProps, mergeStyles, useTheme, MessageBar, MessageBarType } from '@fluentui/react';
import { OptionSetDefinition } from '@definitions/OptionSetDefinition';
import * as queryString from 'query-string';
import { history } from '@providers/AppProvider';
import { QueryData } from '@pages/Control/interfaces';
import { RibbonDefinition, RibbonLocationFilters } from '@definitions/RibbonDefinition';
import { CommandBar } from '@talxis/react-components';
import { DataType } from '@ComponentFramework/interfaces/DataType';
import cloneDeep from 'lodash/cloneDeep';
import { XrmFormType } from '../../../../Xrm/XrmFormType';
import { LocalizeLabel } from '@localization/helpers';
import { ICreateRequest, IUpdateRequest, WebApiErrors } from '@ComponentFramework/PropertyClasses/WebApi';
import { UserSettings } from '@src/app/classes/UserSettings';

export const FormContext = createContext<IFormContext | null>(null);

class XrmUi implements Xrm.Ui {
    public _tabs: React.MutableRefObject<IForm.Tab[]>;
    public _setTabVisible: (name: string, visible: boolean) => void;
    public _setSectionVisible: (tabName: string, name: string, visible: boolean) => void;
    public _setSectionLabel: (tabName: string, name: string, label: string) => void;
    public _setTabLabel: (name: string, label: string) => void;
    public _setTabFocus: (id: string) => void;
    public _currentTabIdRef: React.MutableRefObject<string>;
    private _formContext: XrmFormContext;
    private _formNotifications: React.MutableRefObject<IFormNotification[]>;
    private _setFormNotifications: (notifications: IFormNotification[]) => void;
    private _refreshRibbon: () => void;

    constructor(
        tabs: React.MutableRefObject<IForm.Tab[]>,
        setTabVisible: (name: string, visible: boolean) => void,
        setSectionVisible: (tabName: string, name: string, visible: boolean) => void,
        setSectionLabel: (tabName: string, name: string, label: string) => void,
        setTabLabel: (name: string, label: string) => void,
        setTabFocus: (id: string) => void,
        currentTabIdRef: React.MutableRefObject<string>,
        formContext: XrmFormContext,
        formNotifications: React.MutableRefObject<IFormNotification[]>,
        setFormNotifications: (notifications: IFormNotification[]) => void,
        refreshRibbon: () => void
    ) {
        this._formContext = formContext;
        // TODO: Maybe we don't need to keep the entire tabs object here
        this._tabs = tabs;
        this._setTabVisible = setTabVisible;
        this._setSectionVisible = setSectionVisible;
        this._setSectionLabel = setSectionLabel;
        this._setTabLabel = setTabLabel;
        this._setTabFocus = setTabFocus;
        this._currentTabIdRef = currentTabIdRef;
        this._formNotifications = formNotifications;
        this._setFormNotifications = setFormNotifications;
        this._refreshRibbon = refreshRibbon;

        let that = this;
        this.tabs = {
            get(arg1?: any): any {
                if (typeof arg1 === "string") {
                    const tab = that._tabs.current.find(x => x.name == arg1);
                    if (!tab) throw new Error(`Tab with ${arg1} name not found!`);
                    return that._mapTabObject(tab);
                }
                else if (!arg1) {
                    return that._tabs.current.map(x => that._mapTabObject(x));
                }
            },
            forEach(delegate: Xrm.Collection.IterativeDelegate<Xrm.Controls.Tab>): void {
                throw new Error("Not implemented!");
            },
            getLength(): number {
                return that._tabs.current.length;
            }
        };
    }

    // TODO: We should do all lookups in realtime in _tabs object so we prevent stale values
    private _mapTabObject(tab: IForm.Tab): Xrm.Controls.Tab {
        let that = this;
        return {
            setVisible: (visible: boolean): void => {
                that._setTabVisible(tab.name, visible);
            },
            getVisible: (): boolean => {
                const found = that._tabs.current.find(x => x.name == tab.name);
                return found.visible;
            },
            setLabel(label: string): void {
                that._setTabLabel(tab.name, label);
            },
            setFocus: (): void => {
                that._setTabFocus(tab.id);
            },
            getName: (): string => {
                return tab.name;
            },
            sections: {
                get(arg1?: any): any {
                    if (arg1) {
                        const foundTab = that._tabs.current.find(x => x.name == tab.name);
                        const section = foundTab.columns.find(x => x.sections.some(z => z.name === arg1)).sections.find(x => x.name === arg1);
                        if (!section) return null;
                        return that._mapSectionObject(tab.name, section);
                    }
                    else {
                        const foundTab = that._tabs.current.find(x => x.name == tab.name);
                        return foundTab.columns[0].sections.map(x => that._mapSectionObject(tab.name, x));
                    }
                }
            },
            getDisplayState: (): Xrm.DisplayState => {
                if (that._currentTabIdRef.current === tab.id) {
                    return "expanded";
                }
                else {
                    return "collapsed";
                }
            }
        } as Xrm.Controls.Tab;
    }

    // TODO: We should do all lookups in realtime in _tabs object so we prevent stale values
    private _mapSectionObject(tabName: string, section: IForm.Section): Xrm.Controls.Section {
        let that = this;
        return {
            getName: (): string => {
                return section.name;
            },
            setLabel: (label: string) => {
                that._setSectionLabel(tabName, section.name, label);
            },
            setVisible: (visible: boolean): void => {
                that._setSectionVisible(tabName, section.name, visible);
            },
            controls: {
                get(arg1?: any): any {
                    if (arg1) {
                        throw new Error("Getting controls by name not implemented!");
                    }
                    else {
                        return section.rows[0].cells.map(x => that._mapCellObject(x));
                    }
                }
            }
        } as Xrm.Controls.Section;
    }

    // TODO: We should do all lookups in realtime in _tabs object so we prevent stale values
    private _mapCellObject(cell: IForm.Cell): Xrm.Controls.Control {
        return {
            getName: (): string => {
                return cell.control.id;
            }
        } as Xrm.Controls.Control;
    }

    setFormNotification(message: string, level: Xrm.FormNotificationLevel, uniqueId: string): boolean {
        this._formNotifications.current.push({
            message: message,
            level: level,
            uniqueId: uniqueId
        });
        this._setFormNotifications([...this._formNotifications.current]);

        return true;
    }
    clearFormNotification(uniqueId: string): boolean {
        this._formNotifications.current = this._formNotifications.current.filter(x => x.uniqueId !== uniqueId);
        this._setFormNotifications([...this._formNotifications.current]);

        return true;
    }
    close(): void {
        //TODO: add form name as param
        window.TALXIS.Portal.Context.closeFormDialog(null, this._formContext._entity);
    }
    getFormType(): XrmEnum.FormType {
        if (this._formContext.data?.entity.getEntityName() === undefined) { return XrmFormType.Dialog as any; }
        else if (!this._formContext.data.entity?.getId()) { return XrmFormType.Create as any; }
        else if (this._formContext.data.entity?.getId() && this._formContext?.getAttribute('statecode')?.getValue() !== 0) { return XrmFormType.Disabled as any; }
        else if (this._formContext.data.entity?.getId()) { return XrmFormType.Update as any; }
        return XrmFormType.Undefined as any;
    }
    getViewPortHeight(): number {
        throw new Error("Not implemented!");
    }
    getViewPortWidth(): number {
        throw new Error("Not implemented!");
    }
    refreshRibbon(refreshAll?: boolean): void {
        this._refreshRibbon();
    }
    setFormEntityName(name: string): void {
        throw new Error("Not implemented!");
    }
    process: Xrm.Controls.ProcessControl;
    controls: Xrm.Collection.ItemCollection<Xrm.Controls.Control>;
    formSelector: Xrm.Controls.FormSelector = {
        getCurrentItem: () => {
            return {
                getId: () => {
                    return this._formContext._formId;
                },
                getLabel() {
                    throw new Error("Not implemented!");
                },
                navigate() {
                    throw new Error("Not implemented!");
                },
                getVisible() {
                    throw new Error("Not implemented!");
                },
                setVisible() {
                    throw new Error("Not implemented!");
                }
            };
        },
        items: null
    };
    navigation: Xrm.Controls.Navigation;
    tabs: Xrm.Collection.ItemCollection<Xrm.Controls.Tab>;
    quickForms: Xrm.Collection.ItemCollection<Xrm.Controls.QuickFormControl>;
    footerSection: undefined;
    headerSection: undefined;
}

export class XrmFormContext implements Xrm.FormContext {
    private _entityName: string;
    private _entityId: string;
    public _entity: ComponentFramework.WebApi.Entity = null;
    public _originalEntity: ComponentFramework.WebApi.Entity = null;
    public _childFormContextsRef: React.MutableRefObject<{ [datafieldname: string]: Xrm.FormContext; }>
    public _entityChanges: ComponentFramework.WebApi.Entity = null;
    public _tabs: React.MutableRefObject<IForm.Tab[]> = null;
    public _header: React.MutableRefObject<IForm.Header> = null;
    public _setEntityValue: (key: string, value: any) => void;
    public _setTabs: (tabs: IForm.Tab[]) => void;
    public _formId: string;
    public _saveForm: () => Promise<void | Xrm.CreateResponse>;
    public _refreshForm: () => Promise<void>;
    public _childControlsRef: React.MutableRefObject<{ [name: string]: IChildControl }>;
    public _onSaveRef: React.MutableRefObject<((context: Xrm.Events.SaveEventContext) => Promise<void> | void)[]>;
    public _onPostSaveRef: React.MutableRefObject<Xrm.Events.ContextSensitiveHandler[]>;
    public _attributeConfigurationRef: React.MutableRefObject<{ [name: string]: IAttributeConfiguration }>;
    public _setRequiredLevel: (attributeName: string, level: Xrm.Attributes.RequirementLevel) => void;
    public _entityDefinitionRef: React.MutableRefObject<IEntityDefinition>;
    private _isFormValid: () => boolean;
    public _updateAttributeConfiguration: () => void;

    public static isTalxisPortal: boolean = true;

    constructor(
        formId: string,
        tabs: React.MutableRefObject<IForm.Tab[]>,
        header: React.MutableRefObject<IForm.Header>,
        setEntityValue: (key: string, value: any) => void,
        setTabVisible: (name: string, visible: boolean) => void,
        setSectionVisible: (tabName: string, name: string, visible: boolean) => void,
        setSectionLabel: (tabName: string, name: string, label: string) => void,
        setTabLabel: (name: string, label: string) => void,
        setTabFocus: (id: string) => void,
        setTabs: (tabs: IForm.Tab[]) => void,
        saveForm: () => Promise<void | Xrm.CreateResponse>,
        refreshForm: () => Promise<void>,
        childControlsRef: React.MutableRefObject<{ [name: string]: IChildControl }>,
        onPostSaveRef: React.MutableRefObject<Xrm.Events.ContextSensitiveHandler[]>,
        onSaveRef: React.MutableRefObject<((context: Xrm.Events.SaveEventContext) => Promise<void> | void)[]>,
        currentTabIdRef: React.MutableRefObject<string>,
        attributeConfigurationRef: React.MutableRefObject<{ [name: string]: IAttributeConfiguration }>,
        entityDefinitionRef: React.MutableRefObject<IEntityDefinition>,
        setRequiredLevel: (attributeName: string, level: Xrm.Attributes.RequirementLevel) => void,
        isFormValid: () => boolean,
        updateAttributeConfiguration: () => void,
        childFormContextsRef: React.MutableRefObject<{ [datafieldname: string]: Xrm.FormContext; }>,
        formNotifications: React.MutableRefObject<IFormNotification[]>,
        setFormNotifications: (notifications: IFormNotification[]) => void,
        refreshRibbon: () => void,
        entityName?: string,
        entityId?: string
    ) {
        this._entityName = entityName;
        this._entityId = entityId;
        this._setEntityValue = setEntityValue;
        this._setTabs = setTabs;
        this._saveForm = saveForm;
        this._refreshForm = refreshForm;
        this._tabs = tabs;
        this._header = header;
        this._formId = formId;
        this._childControlsRef = childControlsRef;
        this._onPostSaveRef = onPostSaveRef;
        this._attributeConfigurationRef = attributeConfigurationRef;
        this._onSaveRef = onSaveRef;
        this._setRequiredLevel = setRequiredLevel;
        this._isFormValid = isFormValid;
        this._entityDefinitionRef = entityDefinitionRef;
        this.ui = new XrmUi(tabs, setTabVisible, setSectionVisible, setSectionLabel, setTabLabel, setTabFocus, currentTabIdRef, this, formNotifications, setFormNotifications, refreshRibbon);
        this._updateAttributeConfiguration = updateAttributeConfiguration;
        this._childFormContextsRef = childFormContextsRef;
    }

    // TODO: This shouldn't be any, but Xrm.Data
    public data: any = {
        getIsDirty: (): boolean => {
            //check if child forms have unsaved changes
            for (const datafieldname of Object.keys(this._childFormContextsRef.current)) {
                const formContext = this._childFormContextsRef.current[datafieldname];
                if (formContext.data.getIsDirty()) {
                    return true;
                }
            }
            let hasChanges = false;
            for (const [property, value] of Object.entries(this._entityChanges)) {
                let originalValue = this._originalEntity[property];
                if (
                    // Lookups are stored as arrays of LookupValue, so we need to do deep comparison for proper change detection
                    (originalValue != value && (!Array.isArray(value) || !Array.isArray(originalValue))) ||
                    (Array.isArray(value) && Array.isArray(originalValue) && (
                        value.filter((val1: ComponentFramework.LookupValue) => !originalValue.find((val2: ComponentFramework.LookupValue) => val1.id === val2.id)).length > 0 ||
                        originalValue.filter((val1: ComponentFramework.LookupValue) => !value.find((val2: ComponentFramework.LookupValue) => val1.id === val2.id)).length > 0))
                ) {
                    const field = this._entityDefinitionRef.current?.Attributes.find(x => x.LogicalName === property);

                    if (field && field.AttributeType === "DateTime") {
                        if (new Date(originalValue).getTime() === new Date(value).getTime()) {
                            break;
                        }
                    }
                    hasChanges = true;
                    break;
                }
            }
            if (this._entityChanges.current == {} || !hasChanges) {
                return false;
            }
            else {
                return true;
            }
        },
        attributes: {
            get: (attributeName: string) => {
                return this.getAttribute(attributeName);
            }
        },
        entity: {
            getEntityName: () => {
                return this._entityName;
            },
            addOnPostSave: (handler: Xrm.Events.ContextSensitiveHandler): void => {
                this._onPostSaveRef.current.push(handler);
            },
            removeOnPostSave: (handler: Xrm.Events.ContextSensitiveHandler): void => {
                const found = this._onPostSaveRef.current.indexOf(handler);
                if (found !== -1) {
                    this._onPostSaveRef.current.splice(found, 1);
                }
            },
            addOnSave: (handler: Xrm.Events.ContextSensitiveHandler): void => {
                this._onSaveRef.current.push(handler);
            },
            removeOnSave: (handler: Xrm.Events.ContextSensitiveHandler): void => {
                const found = this._onSaveRef.current.indexOf(handler);
                if (found !== -1) {
                    this._onSaveRef.current.splice(found, 1);
                }
            },
            isValid: (): boolean => {
                return this._isFormValid();
            },
            getId: (): string => {
                return this._entityId;
            },
            getPrimaryAttributeValue: () => {
                return this._entity[this._entityDefinitionRef.current.PrimaryNameAttribute];
            }
        },
        save: async (saveOptions?: Xrm.SaveOptions): Promise<void | Xrm.CreateResponse> => {
            return this._saveForm();
        },
        refresh: async (save: boolean): Promise<void> => {
            if (save) {
                await this._saveForm();
            }
            await this._refreshForm();
        }
    };
    public ui: Xrm.Ui = null;

    public getAttribute(): Xrm.Attributes.Attribute[];
    public getAttribute<T extends Xrm.Attributes.Attribute>(attributeName: string): T;
    public getAttribute(attributeName: string): Xrm.Attributes.Attribute;
    public getAttribute(index: number): Xrm.Attributes.Attribute;
    public getAttribute(delegateFunction: Xrm.Collection.MatchingDelegate<Xrm.Attributes.Attribute>): Xrm.Attributes.Attribute[];
    public getAttribute(arg1?: any): any {
        if (typeof arg1 === "string") {
            return {
                getOptions: () => {
                    if (this._entityDefinitionRef.current && this._entityDefinitionRef.current.Attributes.find(x => x.LogicalName === arg1)?.AttributeTypeName?.Value === "MultiSelectPicklistType") {
                        const allOptions = this._attributeConfigurationRef.current[arg1]?.options;
                        const options: Xrm.OptionSetValue[] = [];
                        for (const option of allOptions) {
                            options.push({
                                text: option.Label.UserLocalizedLabel.Label,
                                value: option.Value
                            });
                        }
                        return options;
                    }
                    else {
                        throw new Error("Not implemented, target attribute is not OptionSet!");
                    }
                },
                getValue: () => {
                    const attribute = this._entityDefinitionRef.current?.Attributes.find(x => x.LogicalName === arg1);
                    let control: any;
                    if (!attribute) {
                        control = this.getControl(arg1);
                    }
                    if (
                        (attribute && (attribute.AttributeType === "Lookup" || attribute.AttributeType === "Customer" || attribute.AttributeType === "Owner")) ||
                        // This handles case of dialogs where we currently don't have any attribute configuration set yet
                        (this._entityDefinitionRef.current === null && isLookupSoft(this._entity[arg1]))
                    ) {
                        const value = this._entity[arg1];
                        if (!value || !Array.isArray(value)) {
                            return value;
                        }
                        else {
                            const lookupValue: Xrm.LookupValue[] = cloneDeep(value);
                            lookupValue.forEach(x => x.id = formatXrmGuid(x.id));
                            return lookupValue;
                        }
                    }
                    else if (attribute && attribute.AttributeType === "DateTime") {
                        if (this._entity[arg1] === null || this._entity[arg1] === undefined || this._entity[arg1] === "") return null;
                        let date = new Date(this._entity[arg1]);
                        if (attribute.DateTimeBehavior.Value === "TimeZoneIndependent" || attribute.DateTimeBehavior.Value === "DateOnly") {
                            const offset = UserSettings.getLocalTimeZoneOffset();
                            date = new Date(date.getTime() + offset);
                        }
                        else if (attribute.DateTimeBehavior.Value === "UserLocal" && attribute.Format === "DateOnly") {
                            let offset = UserSettings.getLocalTimeZoneOffset();

                            if (offset < -1) {
                                offset *= -1;
                            }

                            date = new Date(date.getTime() + offset);
                            date.setHours(0);
                        }

                        return date;
                    }
                    else {
                        return this._entity[arg1];
                    }
                },
                setValue: (value: any) => {
                    // TODO: Check type of value, if it matches well-known formats else transform it to string, due to: https://dev.azure.com/thenetworg/INT0006/_git/TALXIS?path=%2Fsrc%2FAreas%2FDistribution%2FSales%2FApps.Default%2FTS%2Ftalxis_opportunityheader%2FDialogs.ts&version=GBdevelop&line=11&lineEnd=12&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents
                    this._setEntityValue(arg1, value);
                },
                setRequiredLevel: (level: Xrm.Attributes.RequirementLevel) => {
                    this._attributeConfigurationRef.current[arg1].customRequiredLevel = level;
                    this._setRequiredLevel(arg1, level);
                    this._updateAttributeConfiguration();
                },
                getRequiredLevel: (): Xrm.Attributes.RequirementLevel => {
                    switch (getRequiredLevel(this._attributeConfigurationRef.current[arg1])) {
                        case 1:
                        case 2:
                            return "required";
                        case 3:
                            return "recommended";
                        case -1:
                        case 0:
                        default:
                            return "none";

                    }
                },
                getName: () => {
                    return arg1;
                }
            } as Xrm.Attributes.Attribute | Xrm.Attributes.OptionSetAttribute;
        }

        throw new Error("Not implemented, reference attribute by its name!");
    }

    private _findControl(tabs: IForm.Tab[], header: IForm.Header, id: string): IForm.Control {
        // TODO: Can we do this nicer? Use JPath
        if (header) {
            for (const rows of header?.rows) {
                for (const cell of rows?.cells) {
                    if (cell?.control?.id === id) {
                        return cell.control;
                    }
                }
            }
        }
        for (const tab of tabs) {
            for (const column of tab.columns) {
                for (const section of column.sections) {
                    for (const row of section.rows) {
                        for (const cell of row.cells) {
                            if (cell?.control?.id === id) {
                                return cell.control;
                            }
                        }
                    }
                }
                for (const row of tab.tabFooter?.rows ?? []) {
                    for (const cell of row.cells) {
                        if (cell?.control?.id === id) {
                            return cell.control;
                        }
                    }
                }
            }
        }

        return null;
    }

    private _findCell(tabs: IForm.Tab[], header: IForm.Header, id: string): IForm.Cell {
        for (const row of header?.rows ?? []) {
            for (const cell of row?.cells) {
                if (cell?.control?.id === id) {
                    return cell;
                }
            }
        }
        // TODO: Can we do this nicer? Use JPath
        for (const tab of tabs) {
            for (const column of tab.columns) {
                for (const section of column.sections) {
                    for (const row of section.rows) {
                        for (const cell of row.cells) {
                            if (cell?.control?.id === id) {
                                return cell;
                            }
                        }
                    }
                }
                for (const row of tab.tabFooter?.rows ?? []) {
                    for (const cell of row.cells) {
                        if (cell?.control?.id === id) {
                            return cell;
                        }
                    }
                }
            }
        }

        return null;
    }

    public getControl(): Xrm.Controls.Control[];
    public getControl<T extends Xrm.Controls.Control>(controlName: string): T;
    public getControl(controlName: string): Xrm.Controls.Control;
    public getControl<T extends Xrm.Controls.Control>(index: number): T;
    public getControl(index: number): Xrm.Controls.Control;
    public getControl(delegateFunction: Xrm.Collection.MatchingDelegate<Xrm.Controls.Control>): Xrm.Controls.Control[];
    public getControl(arg1?: any): any {
        if (typeof arg1 === "string") {
            const that = this;
            const control = this._findControl(that._tabs.current, that._header.current, arg1);
            if (!control) return null;

            return {
                getVisible: () => {
                    const control = that._findControl(that._tabs.current, that._header.current, arg1);
                    return control.visible;
                },
                setVisible: (visible: boolean) => {
                    const control = that._findControl(that._tabs.current, that._header.current, arg1);
                    console.log(`Setting ${control.id} control to visible: ${visible}.`);
                    // Update by reference
                    control.visible = visible;
                    this._setTabs(this._tabs.current);
                },
                getDisabled: () => {
                    const control = that._findControl(that._tabs.current, that._header.current, arg1);
                    return control.disabled;
                },
                setDisabled: (disabled: boolean) => {
                    const control = that._findControl(that._tabs.current, that._header.current, arg1);
                    // Update by reference
                    control.disabled = disabled;
                    console.log(`Setting ${control.id} control to disabled: ${disabled}.`);
                    // rerender tabs
                    this._setTabs(this._tabs.current);
                },
                getLabel: () => {
                    const cell = that._findCell(that._tabs.current, that._header.current, arg1);
                    return cell.label;
                },
                setLabel: (label: string) => {
                    const cell = that._findCell(that._tabs.current, that._header.current, arg1);
                    cell.label = label;
                    // rerender tabs
                    this._setTabs(this._tabs.current);
                },
                getAttribute: () => {
                    const control = that._findControl(that._tabs.current, that._header.current, arg1);
                    return {
                        getValue: () => {
                            return this.getAttribute(control.datafieldname).getValue();
                        },
                        setValue: (value: any) => {
                            this.getAttribute(control.datafieldname).setValue(value);
                        },
                        setRequiredLevel: (level: Xrm.Attributes.RequirementLevel) => {
                            this.getAttribute(control.datafieldname).setRequiredLevel(level);
                        },
                        getRequiredLevel: () => {
                            return this.getAttribute(control.datafieldname).getRequiredLevel();
                        }
                    } as Xrm.Attributes.Attribute;
                },
                refresh: () => {
                    const control = that._childControlsRef.current[arg1];
                    if (control) {
                        control.gridRefresh();
                    }
                    else {
                        throw new Error(`Control with ${arg1} name not found in registered childControls!`);
                    }
                },
                addOption: (option: Xrm.OptionSetValue, index?: number) => {
                    this._attributeConfigurationRef.current[arg1].options.splice(index ?? -1, 0, {
                        Description: {
                            LocalizedLabels: [],
                            UserLocalizedLabel: null
                        },
                        Value: option.value,
                        Label: {
                            LocalizedLabels: [{
                                Label: option.text,
                                LanguageCode: 1033
                            }],
                            UserLocalizedLabel: null
                        }
                    });
                    this._updateAttributeConfiguration();
                },
                clearOptions: () => {
                    this._attributeConfigurationRef.current[arg1].options = [];
                    this._updateAttributeConfiguration();
                },
                removeOption: (value: number) => {
                    const index = this._attributeConfigurationRef.current[arg1].options?.findIndex(x => x.Value === value);
                    this._attributeConfigurationRef.current[arg1].options?.splice(index, 1);
                    this._updateAttributeConfiguration();
                },
                setShowTime: (value: boolean) => {
                    const control = that._findControl(that._tabs.current, that._header.current, arg1);
                    if (control?.bindings?.Format) {
                        control.bindings.Format.value = value ? "DateAndTime" : "Date";
                    } else {
                        control.bindings.Format = { isStatic: true, value: value ? "DateAndTime" : "Date" };
                    }
                },
                getShowTime: (): boolean => {
                    const format = that._findControl(that._tabs.current, that._header.current, arg1).bindings?.Format?.value;
                    return format === "DateAndTime";
                },
                controlDescriptor: {
                    ClassId: {
                        guid: (control.classId ? sanitizeGuid(control.classId).toLowerCase() : null)
                    }
                }
            } as Partial<Xrm.Controls.StandardControl>;
        }

        throw new Error("Not implemented, reference control by its controlName!");
    }
}

export interface XrmEventContext extends Xrm.Events.EventContext {
    formContext?: XrmFormContext;
}

export interface IFormNotification {
    message: string;
    level: Xrm.FormNotificationLevel;
    uniqueId: string;
}

enum ClassId {
    DateTime = "5b773807-9fb2-42db-97c3-7a91eff8adff"
}

export const isFieldValid = (isRequired: ComponentFramework.PropertyHelper.Types.RequiredLevel, isDisabled: boolean, isVisible: boolean, type: DataType, entity: ComponentFramework.WebApi.Entity, primaryBoundField: string) => {
    if ((isRequired === 1 || isRequired === 2) && !isDisabled && isVisible &&
        (
            (type !== "Lookup.Simple" && (
                entity[primaryBoundField] === null ||
                entity[primaryBoundField] === undefined ||
                entity[primaryBoundField] === "" ||
                Number.isNaN(entity[primaryBoundField])
            )) ||
            // Support for lookup values until we properly represent lookups
            (type === "Lookup.Simple" &&
                (entity[primaryBoundField] as ComponentFramework.LookupValue[]) === null ||
                (entity[primaryBoundField] as ComponentFramework.LookupValue[]) === undefined ||
                (entity[primaryBoundField] as ComponentFramework.LookupValue[])?.length === 0)
        )
    ) {
        return false;
    }

    if (type === "Currency" && Number.isNaN(entity[primaryBoundField])) {
        return false;
    }

    return true;
};

export const Form: React.FC<IFormProps> = (props) => {
    const [form, setForm] = useState<IForm.Root>(null);
    const [tabs, setTabs] = useState<IForm.Tab[]>(null);
    const [currentTab, setCurrentTab] = useState<IForm.Tab>(null);
    const [error, setError] = useState<Error>(null);
    const [formUpdated, setFormUpdated] = useState<boolean>(false);
    const [ribbonCommandBarProps, setRibbonCommandBarProps] = useState<ICommandBarProps>(null);
    const [isEditable, setIsEditable] = useState<boolean>(false);
    const [shouldRefresh, setShouldRefresh] = useState<boolean>(false);
    const [attributeConfiguration, setAttributeConfiguration] = useState<{ [name: string]: IAttributeConfiguration }>({});
    const [validate, setValidate] = useState<boolean>(false);
    const theme = useTheme();
    const [entity, setEntity] = useState<ComponentFramework.WebApi.Entity>(null);
    const [entityChanges, setEntityChanges] = useState<ComponentFramework.WebApi.Entity>({});
    const [entityId, setEntityId] = useState<string>(props.entityId);

    const formRef = useRef<IForm.Root>(null);
    const childFormContextsRef = useRef<{
        [datafieldname: string]: Xrm.FormContext;
    }>({});
    const xrmExecutionContextRef = useRef<XrmEventContext>(null);
    const currentTabIdRef = useRef<string>(null);
    const previousEntityChangesRef = useRef<ComponentFramework.WebApi.Entity>({});
    const entityRef = useRef<ComponentFramework.WebApi.Entity>({});
    const entityChangesRef = useRef<ComponentFramework.WebApi.Entity>({});
    const onLoadExecutedRef = useRef<boolean>(false);
    const tabsRef = useRef<IForm.Tab[]>(null);
    const headerRef = useRef<IForm.Header>(null);
    const entityDefinitionRef = useRef<IEntityDefinition>(null);
    const childControlsRef = useRef<{ [name: string]: IChildControl }>({});
    const onPostSaveRef = useRef<Xrm.Events.ContextSensitiveHandler[]>([]);
    const onSaveRef = useRef<((context: Xrm.Events.SaveEventContext) => Promise<void> | void)[]>([]);
    const attributeConfigurationRef = useRef<{ [name: string]: IAttributeConfiguration }>({});
    const [isMobile, setIsMobile] = useState<boolean>(false);
    const formNotificationsRef = useRef<IFormNotification[]>([]);
    const [formNotifications, setFormNotifications] = useState<IFormNotification[]>([]);

    const onResize = useCallback((width: number) => {
        if (window.innerWidth <= 768) {
            setIsMobile(true);
            return;
        }
        setIsMobile(false);
    }, []);

    const { ref } = useResizeDetector({ onResize });

    // TODO: IFormProps should have QuickForms input and parsing should be done in the code itself: "<QuickForms><QuickFormIds><QuickFormId entityname="account">E9388EE6-C985-4F4C-9B2A-BEB5044FE3AC</QuickFormId></QuickFormIds></QuickForms>"
    /*
    <property name="value" display-name-key="value_name" of-type-group="lookup" usage="bound" required="true" hidden="true" />
    <property name="QuickForms" required="true" display-name-key="forms_name" usage="input" of-type="SingleLine.Text" />
    */

    useEffect(() => {
        if (xrmExecutionContextRef.current != null) {
            const formContext = xrmExecutionContextRef.current.getFormContext() as XrmFormContext;
            formContext._entity = { ...entityRef.current, ...entityChangesRef.current };
            formContext._entityChanges = entityChangesRef.current;
            formContext._originalEntity = { ...entity };

            if (props.isQuickCreate || props.formUniqueName) {
                // TODO: We should serialize the entity here,
                props.fireEvent("onDataChange", { ...formContext._entity });
            }
        }
    }, [entity, entityChanges]);

    useEffect(() => {
        setEntityId(props.entityId);
    }, [props.entityId]);

    useEffect(() => {
        props.fireEvent("__formNotificationsChanged", [...formNotificationsRef.current]);
    }, [formNotifications]);

    const getAllControls = (tabs: IForm.Tab[]): IForm.Control[] => {
        const controls: IForm.Control[] = [];

        for (const tab of tabs) {
            for (const column of tab.columns) {
                for (const section of column.sections) {
                    for (const row of section.rows) {
                        for (const cell of row.cells) {
                            if (cell.control) {
                                controls.push(cell.control);
                            }
                        }
                    }
                }
                for (const row of tab.tabFooter?.rows ?? []) {
                    for (const cell of row.cells) {
                        if (cell.control) {
                            controls.push(cell.control);
                        }
                    }
                }
            }
        }

        return controls;
    };

    const ribbonEnabled = () => {
        if (!props.entityName) {
            return false;
        }
        if (props.formUniqueName) {
            return false;
        }
        if (!props.enableRibbon) {
            return false;
        }
        return true;
    };

    const _isEditable = (tabs: IForm.Tab[]): boolean => {
        // TODO: Can we do this nicer? Use JPath
        for (const tab of tabs.filter(x => x.visible === true)) {
            for (const column of tab.columns) {
                for (const section of column.sections) {
                    for (const row of section.rows) {
                        for (const cell of row.cells) {
                            if (cell?.control?.type === FormControlType.Field &&
                                cell?.control?.disabled === false &&
                                cell?.control?.visible === true &&
                                cell?.control?.name?.indexOf("FormControl") === -1 &&
                                cell?.control?.name?.indexOf("TALXIS.PCF.Portal.Form") === -1) {
                                console.log(`Attribute ${cell.label} - ${cell.control.datafieldname} - ${cell.control.name} is editable!`);
                                return true;
                            }
                        }
                    }
                }
            }
        }

        return false;
    };

    /**
     * Mapping lookups by reference
     */
    const mapLookups = (entity: ComponentFramework.WebApi.Entity) => {
        for (const key of Object.keys(entity)) {
            if (key.startsWith("_") && key.endsWith("_value")) {
                const logicalName = key.replace("_value", "").replace("_", "");
                const attribute = entityDefinitionRef.current?.Attributes?.find(x => x.LogicalName === logicalName);
                if (attribute?.AttributeType === "Lookup" || attribute?.AttributeType === "Owner" || attribute?.AttributeType === "Customer") {
                    if (entity[key]) {
                        const lookupValue: ComponentFramework.LookupValue = {
                            id: entity[key],
                            entityType: entity[`${key}@Microsoft.Dynamics.CRM.lookuplogicalname`],
                            name: entity[`${key}@OData.Community.Display.V1.FormattedValue`]
                        };
                        entity[logicalName] = [lookupValue];
                    }
                    else {
                        entity[logicalName] = [];
                    }
                    delete entity[key];
                    delete entity[`${key}@Microsoft.Dynamics.CRM.lookuplogicalname`];
                    delete entity[`${key}@OData.Community.Display.V1.FormattedValue`];
                }
            }
        }
    };

    const mapDateTimeAndDateOnly = (entityDefinition: IEntityDefinition, entity: ComponentFramework.WebApi.Entity) => {
        for (const key of Object.keys(entity)) {
            const attribute = entityDefinition.Attributes.find(x => x.LogicalName === key);
            if (attribute && attribute.AttributeType === "DateTime") {
                if (entity[key]) {
                    entity[key] = new Date(entity[key]);
                }
            }
        }
    };

    const mapMultiSelectPicklists = (entityDefinition: IEntityDefinition, entity: ComponentFramework.WebApi.Entity) => {
        for (const key of Object.keys(entity)) {
            const attribute = entityDefinition.Attributes.find(x => x.LogicalName === key);
            if (attribute && attribute.AttributeType === "Virtual" && attribute.AttributeTypeName?.Value === "MultiSelectPicklistType") {
                if (entity[key] && !Array.isArray(entity[key])) {
                    entity[key] = (entity[key] as string).split(",").map(x => parseInt(x));
                }
            }
        }
    };

    const mapPartyListLookups = async (entity: ComponentFramework.WebApi.Entity, partyListRelationshipName: string) => {
        const key = Object.keys(entity).find(x => x === partyListRelationshipName);
        if (key) {
            for (const record of entity[key]) {
                let lookupValue: ComponentFramework.LookupValue[] = [];
                const lookupEntityName = record[`_partyid_value@Microsoft.Dynamics.CRM.lookuplogicalname`];
                const lookupEntityDefinition = await EntityDefinition.getAsync(lookupEntityName);
                let associatedNavigationProperty = record[`_partyid_value@Microsoft.Dynamics.CRM.associatednavigationproperty`];
                const id = record["_partyid_value"];
                const formattedValue = record[`_partyid_value@OData.Community.Display.V1.FormattedValue`];
                const participationMask: number = record["participationtypemask"];
                const attributeLogicalName = ParticipationTypeMasktoType[participationMask];

                if (!associatedNavigationProperty) {
                    associatedNavigationProperty = lookupEntityDefinition.OneToManyRelationships.find(x => x.ReferencedEntity == lookupEntityName && x.ReferencingAttribute == "partyid")?.ReferencingEntityNavigationPropertyName ??
                        lookupEntityDefinition.ManyToOneRelationships.find(x => x.ReferencedEntity == lookupEntityName && x.ReferencingAttribute == "partyid")?.ReferencingEntityNavigationPropertyName;
                }
                if (entity[attributeLogicalName]) {
                    lookupValue = entity[attributeLogicalName];
                }
                lookupValue.push({
                    id: id,
                    entityType: lookupEntityDefinition.LogicalName,
                    name: formattedValue,
                });
                entity[attributeLogicalName] = lookupValue;
            }
        }
    };

    const getParticipationTypeMasks = (entityName: string, columnName?: string) => {
        const participationTypeMask: { [key: string]: { [key: string]: number } } = {
            appointment: { optionalattendees: 6, organizer: 7, requiredattendees: 5 },
            campaignactivity: { partners: 12, from: 1 }, // Not TESTED - Entity used in Dynamics365
            campaignresponse: { customer: 11, partner: 11, from: 11 }, // Not TESTED - Entity used in Dynamics365
            email: { bcc: 4, cc: 3, from: 1, to: 2 },
            fax: { from: 1, to: 2 },
            letter: { bcc: 4, from: 1, to: 2, cc: 3 },
            phonecall: { from: 1, to: 2 },
            recurringappointmentmaster: { optionalattendees: 6, organizer: 7, requiredattendees: 5 }, // Not tested - The recurrence pattern is not valid. Select a valid recurrence pattern. error when creating record
            serviceappointment: { customers: 11, resources: 10 }, // Not TESTED - Entity used in Dynamics365
            customEntity: { bcc: 4, cc: 3, customers: 11, from: 1, optionalattendees: 6, organizer: 7, partners: 12, requiredattendees: 5, resources: 10, to: 2 }
        };

        if (!Object.keys(participationTypeMask).find(x => x === entityName.toLocaleLowerCase()))
            entityName = "customEntity";

        if (columnName) {
            return participationTypeMask[entityName][columnName];
        } else {
            return participationTypeMask[entityName];
        }
    };

    const assemblePartyListObject = async (excludedPartyListAttribute: number) => {
        const partyListObject: IPartyListObject[] = [];
        const partyListAttributes = getParticipationTypeMasks(props.entityName) as { [key: string]: number };
        for (const attribute in partyListAttributes) {
            if (entityRef.current[attribute] && partyListAttributes[attribute] !== excludedPartyListAttribute) {
                const values = entityRef.current[attribute];
                for (const value of values) {
                    const lookupEntityName = value["entityType"];
                    const lookupEntityDefinition = await EntityDefinition.getAsync(lookupEntityName);
                    const associatedNavigationProperty = lookupEntityDefinition.OneToManyRelationships.find(x => x.ReferencedEntity == lookupEntityName && x.ReferencingAttribute == "partyid")?.ReferencingEntityNavigationPropertyName ??
                        lookupEntityDefinition.ManyToOneRelationships.find(x => x.ReferencedEntity == lookupEntityName && x.ReferencingAttribute == "partyid")?.ReferencingEntityNavigationPropertyName;
                    partyListObject.push({
                        "participationtypemask": partyListAttributes[attribute],
                        [`${associatedNavigationProperty}@odata.bind`]: `/${lookupEntityDefinition.EntitySetName}(${value["id"]})`
                    });
                }
            }
        }
        return partyListObject;
    };

    const getTranslation = (string: string, variables: {} = {}): string => {
        return props.getTranslatedString(string, variables);
    };

    const _onRecordSelect = useCallback((controlId: string, executionContext: Xrm.Events.EventContext) => {
        const event = form?.events.find(x => x.control === controlId && x.name === "onrecordselect");
        if (event) {
            for (const handler of event.handlers) {
                const parameters: any[] = [];
                if (handler.passExecutionContext) {
                    parameters.push(executionContext);
                }
                executeFunctionByName(handler.functionName, window, parameters);
            }
        }
    }, [form]);

    const _onRecordsSelect = useCallback((controlId: string, executionContext: Xrm.Events.EventContext) => {
        const event = form?.events.find(x => x.control === controlId && x.name === "onrecordsselect");
        if (event) {
            for (const handler of event.handlers) {
                const parameters: any[] = [];
                if (handler.passExecutionContext) {
                    parameters.push(executionContext);
                }
                executeFunctionByName(handler.functionName, window, parameters);
            }
        }
    }, [form]);

    const _registerChildControl = useCallback((name: string, control: IChildControl) => {
        childControlsRef.current[name] = control;
    }, []);

    const _setEntityChanges = useCallback((entityChanges: ComponentFramework.WebApi.Entity) => {
        let changed = false;
        for (const [key, value] of Object.entries(entityChanges)) {
            if (value !== entityChangesRef.current[key]) {
                if (value === "") {
                    entityChanges[key] = null;
                }
                changed = true;
                break;
            }
        }
        if (changed) {
            entityChangesRef.current = { ...entityChangesRef.current, ...entityChanges };
            setEntityChanges(entityChangesRef.current);
        }
    }, [setEntityChanges]);

    useEffect(() => {
        if (tabsRef.current != null) {
            const editable = _isEditable(tabsRef.current);
            setIsEditable(editable);
        }
    }, [tabs]);

    useEffect(() => {
        currentTabIdRef.current = currentTab?.id;
        if (currentTab) {
            props.fireEvent('__onFooterUpdate', {
                currentTab: tabsRef.current.find(tab => tab.id === currentTabIdRef.current),
                executionContext: xrmExecutionContextRef.current
            });
            props.fireEvent('__onFormHeaderChanged', headerRef.current);
        }
    }, [currentTab]);

    useEffect(() => {
        // As per https://www.debuggr.io/react-update-unmounted-component/
        let mounted = true;
        const initAsync = async () => {
            try {
                const entityDefinition = props.entityName ? await EntityDefinition.getAsync(props.entityName) : null;
                let expand: string = null;
                let partyListRelationshipName: string = null;
                // If entity contains partylist attributes
                if (entityDefinition?.IsActivity) {
                    const participationTypeMasks = getParticipationTypeMasks(props.entityName); // get participation masks for entity
                    partyListRelationshipName = entityDefinition.OneToManyRelationships.find(x => x.ReferencedEntity == props.entityName && x.ReferencingAttribute == "activityid")?.ReferencedEntityNavigationPropertyName;
                    expand = `?$expand=${partyListRelationshipName}($filter=Microsoft.Dynamics.CRM.In(PropertyName='participationtypemask',PropertyValues=[${Object.values(participationTypeMasks).map(x => { return `'${x}'`; })}]))`;
                }
                //There are two cases when data is not supposed to be loaded - new record form & dialog
                const recordData = (props.entityName && entityId) ? await window.Xrm.WebApi.retrieveRecord(props.entityName, entityId, expand) : {};

                const entityChangesFromExtraQs: ComponentFramework.WebApi.Entity = {};
                entityDefinitionRef.current = entityDefinition;
                mapLookups(recordData);
                mapDateTimeAndDateOnly(entityDefinition, recordData);
                mapMultiSelectPicklists(entityDefinition, recordData);
                if (entityDefinition?.IsActivity) {
                    await mapPartyListLookups(recordData, partyListRelationshipName);
                }

                const extraQsKeys = Object.keys(props.extraqs ?? []);

                // Update current recordData with extraQs passed from Page.
                for (const [property, value] of Object.entries(props.extraqs ?? [])) {
                    // We are in a dialog, so we don't set anything related to lookup from extraQs
                    if (!props.entityName) {
                        recordData[property] = value;
                        entityChangesFromExtraQs[property] = value;
                    }
                    else {
                        const attribute = entityDefinition.Attributes.find(x => x.LogicalName === property);

                        if (attribute) {
                            if (attribute?.AttributeType === "Lookup" || attribute?.AttributeType === "Owner" || attribute?.AttributeType === "Customer" || attribute?.AttributeType === "PartyList") {
                                const lookupValue: ComponentFramework.LookupValue = {
                                    id: sanitizeGuid(value as string),
                                    entityType: props.extraqs[`${property}type`] as string ?? attribute.Targets?.[0],
                                    name: props.extraqs[`${property}name`] as string
                                };
                                recordData[property] = [lookupValue];
                                entityChangesFromExtraQs[property] = [lookupValue];
                            }
                            else if (!attribute.AttributeOf) {
                                //parse Int if we know target attribute is integer or picklist
                                if (attribute?.AttributeType === "Integer" || attribute?.AttributeType === "Picklist") {
                                    const integerValue: number = parseInt(value as string);
                                    recordData[property] = integerValue;
                                    entityChangesFromExtraQs[property] = [integerValue];
                                }
                                else {
                                    recordData[property] = value;
                                    entityChangesFromExtraQs[property] = value;
                                }
                            }
                        }
                    }
                }
                const formMapper = new FormMapper(props.entityName, entityId, props.formId, props.formUniqueName);
                const _form = await formMapper.getForm();
                for (const attribute of entityDefinition?.Attributes ?? []) {
                    attributeConfigurationRef.current[attribute.LogicalName] = {
                        customRequiredLevel: null,
                        entityRequiredLevel: attribute.RequiredLevel.Value
                    };
                    if (attribute.AttributeTypeName.Value === "MultiSelectPicklistType") {
                        const optionsets = await OptionSetDefinition.getMultiSelectOptionSetAsync(props.entityName);
                        attributeConfigurationRef.current[attribute.LogicalName].options = optionsets.value.find(x => x.LogicalName === attribute.LogicalName)?.OptionSet.Options;
                    }

                    // Update boolean fields with default value, unless already provided by extraqs or already set in the record
                    if (attribute?.AttributeType === "Boolean" && !extraQsKeys.includes(attribute.LogicalName) && recordData[attribute.LogicalName] === undefined) {
                        const optionsets = await OptionSetDefinition.getTwoOptionsAsync(props.entityName);

                        recordData[attribute.LogicalName] = optionsets.value.find(x => x.LogicalName === attribute.LogicalName)?.DefaultValue;
                    }

                }
                // When form is dialog, we need to configure attributeConfiguration from id of controls
                // TODO: We should replace this with iterating through control elements in parsed _form instead of _form.formXml
                if (!props.entityName && !entityId) {
                    for (const control of _form.formXml.getElementsByTagName("control")) {
                        const id = control.getAttribute("id");
                        attributeConfigurationRef.current[id] = {
                            customRequiredLevel: null,
                            entityRequiredLevel: control.getAttribute("isrequired") === "true" ? "ApplicationRequired" : "None"
                        };
                    }
                }

                setAttributeConfiguration({ ...attributeConfigurationRef.current });

                // Record is inactive and form should be disabled
                if (props.entityName && props.entityId && recordData["statecode"] === 1) {
                    for (const tab of _form.tabs) {
                        for (const column of tab.columns) {
                            for (const section of column.sections) {
                                for (const row of section.rows) {
                                    for (const cell of row.cells) {
                                        if (cell.control) {
                                            cell.control.disabled = true;
                                        }
                                    }
                                }
                            }
                        }
                    }

                    const optionSets = await OptionSetDefinition.getAsync(props.entityName);
                    const statusCode = optionSets.value.find(x => x.LogicalName == "statuscode").OptionSet.Options.find(x => x.Value === recordData["statuscode"]);
                    const stateCode = optionSets.value.find(x => x.LogicalName === "statecode").OptionSet.Options.find(x => x.Value === recordData["statecode"]);

                    props.fireEvent("__setFormDisabled", { disabled: true, message: `${props.getTranslatedString("recordInactiveNotification", {})} ${LocalizeLabel(statusCode.Label.LocalizedLabels)}` });
                }

                // TODO: Move this into a separate class.
                xrmExecutionContextRef.current = {
                    getFormContext() {
                        if (!this.formContext) {
                            const setEntityValue = (key: string, value: any) => {
                                const attribute = entityDefinitionRef.current?.Attributes.find(x => x.LogicalName === key);
                                let control: any;
                                if (!attribute) {
                                    control = this.formContext.getControl(key);
                                }
                                if (
                                    (attribute && (attribute.AttributeType === "Lookup" || attribute.AttributeType === "Owner" || attribute.AttributeType === "Customer")) ||
                                    // This handles case of dialogs where we currently don't have any attribute configuration set yet
                                    (entityDefinitionRef.current === null && isLookupSoft(value))
                                ) {
                                    if (value === null) {
                                        value = [];
                                    }
                                    else if (Array.isArray(value)) {
                                        const lookupValue: Xrm.LookupValue[] = cloneDeep(value);
                                        lookupValue.forEach(x => x.id = sanitizeGuid(x.id));
                                        value = lookupValue;
                                    }
                                }
                                else if (attribute && attribute.AttributeType === "DateTime") {
                                    let date = new Date(value);
                                    if (attribute.DateTimeBehavior.Value === "TimeZoneIndependent" || attribute.DateTimeBehavior.Value === "DateOnly") {
                                        const offset = UserSettings.getLocalTimeZoneOffset();
                                        date = new Date(date.getTime() + offset);
                                    }
                                    else if (attribute.DateTimeBehavior.Value === "UserLocal" && attribute.Format === "DateOnly") {
                                        let offset = UserSettings.getLocalTimeZoneOffset();

                                        if (offset < -1) {
                                            offset *= -1;
                                        }

                                        date = new Date(date.getTime() + offset);
                                        date.setHours(0);
                                    }

                                    value = date;
                                }

                                // We update values on formContext first, because it is synchronous (JS code will see changes instantly)
                                this.formContext._entityChanges = { ...this.formContext._entityChanges, ...{ [key]: value } };
                                this.formContext._entity = { ...this.formContext._entity, ...this.formContext._entityChanges };

                                entityChangesRef.current = this.formContext._entityChanges;
                                // TODO: Should we check mounted here?
                                setEntityChanges(this.formContext._entityChanges);
                            };
                            const setTabVisible = (name: string, visible: boolean) => {
                                const tab = tabsRef.current.find(x => x.name == name);
                                // Tab visibility being modified via reference, it is available in FormContext instantly.
                                tab.visible = visible;

                                setTabs([...tabsRef.current]);
                            };
                            const setSectionVisible = (tabName: string, name: string, visible: boolean) => {
                                const tab = tabsRef.current.find(x => x.name == tabName);
                                const section = tab.columns.find(x => x.sections.some(z => z.name === name)).sections.find(x => x.name === name);
                                // Section visibility being modified via reference, it is available in FormContext instantly.
                                section.visible = visible;

                                setTabs([...tabsRef.current]);
                            };
                            const setSectionLabel = (tabName: string, name: string, label: string) => {
                                const tab = tabsRef.current.find(x => x.name == tabName);
                                const section = tab.columns.find(x => x.sections.some(z => z.name === name)).sections.find(x => x.name === name);
                                // Section label being modified via reference, it is available in FormContext instantly.
                                section.label = label;

                                setTabs([...tabsRef.current]);
                            };
                            const setTabLabel = (name: string, label: string) => {
                                const tabs = (this.formContext.ui as XrmUi)._tabs;
                                const tab = tabs.current.find(x => x.name == name);
                                // Tab label being modified via reference, it is available in FormContext instantly.
                                const labelMatches = [...label.matchAll(/#(.*)#(.*)/g)];
                                const labelMatchResult = labelMatches.length === 1 ? labelMatches[0] : [];
                                tab.iconName = labelMatchResult.length > 2 ? labelMatchResult[1] : null;
                                tab.label = labelMatchResult.length > 2 ? labelMatchResult[2] : label;

                                // TODO: Should we check mounted here?
                                setTabs([...tabs.current]);
                            };
                            const updateTabs = (updatedTabs: IForm.Tab[]) => {
                                tabsRef.current = [...updatedTabs];

                                setTabs([...updatedTabs]);
                                setCurrentTab({ ...updatedTabs.find(x => x.id == currentTabIdRef.current) });
                            };
                            const setTabFocus = (id: string) => {
                                setCurrentTab({ ...tabsRef.current.find(x => x.id == id) });
                            };
                            const setRequiredLevel = (attributeName: string, level: Xrm.Attributes.RequirementLevel): void => {
                                const attributeConfiguration = attributeConfigurationRef.current[attributeName];
                                if (attributeConfiguration) {
                                    attributeConfiguration.customRequiredLevel = level;
                                }
                                else {
                                    throw new Error(`Attribute ${attributeName} not found!`);
                                }
                                setAttributeConfiguration({ ...attributeConfigurationRef.current });
                            };
                            const updateAttributeConfiguration = () => {
                                setAttributeConfiguration({ ...attributeConfigurationRef.current });
                            };
                            const refreshRibbon = () => {
                                const formContext = this.formContext;
                                if (ribbonEnabled()) {
                                    (async () => {
                                        const ribbon = await RibbonDefinition.getRibbon(props.entityName, RibbonLocationFilters.Form);
                                        setRibbonCommandBarProps(await ribbon.getRibbonCommandBarProps(this.formContext, null, null, false));
                                    })();
                                }
                                props.fireEvent('__refreshRibbon', { formContext });
                            };
                            this.formContext = new XrmFormContext(
                                _form.id,
                                tabsRef,
                                headerRef,
                                setEntityValue,
                                setTabVisible,
                                setSectionVisible,
                                setSectionLabel,
                                setTabLabel,
                                setTabFocus,
                                updateTabs,
                                saveForm,
                                refreshForm,
                                childControlsRef,
                                onPostSaveRef,
                                onSaveRef,
                                currentTabIdRef,
                                attributeConfigurationRef,
                                entityDefinitionRef,
                                setRequiredLevel,
                                isFormValid,
                                updateAttributeConfiguration,
                                childFormContextsRef,
                                formNotificationsRef,
                                setFormNotifications,
                                refreshRibbon,
                                props.entityName,
                                entityId,
                            );
                        }
                        return this.formContext;
                    },
                    getContext: () => { throw new Error("Not implemented in EventContext!"); },
                    getDepth: () => { throw new Error("Not implemented in EventContext!"); },
                    getEventSource: () => { throw new Error("Not implemented in EventContext!"); },
                    getSharedVariable<T>(key: string) {
                        throw new Error("Not implemented in EventContext!");
                    },
                    setSharedVariable<T>(key: string, value: T) {
                        throw new Error("Not implemented in EventContext!");
                    }
                };

                if (mounted) {
                    setEntity(recordData);
                    entityChangesRef.current = entityChangesFromExtraQs;
                    setEntityChanges(entityChangesFromExtraQs);
                    entityRef.current = recordData;
                    if (Object.keys(previousEntityChangesRef.current).length === 0) previousEntityChangesRef.current = recordData;

                    const formContext = xrmExecutionContextRef.current.getFormContext() as XrmFormContext;
                    formContext._entity = recordData;
                    tabsRef.current = _form.tabs;
                    headerRef.current = _form.header;

                    //TODO: performance
                    if (ribbonEnabled()) {
                        const ribbon = await RibbonDefinition.getRibbon(props.entityName, RibbonLocationFilters.Form);
                        setRibbonCommandBarProps(await ribbon.getRibbonCommandBarProps(formContext, null, null, false));
                    }
                    //passing initial formContext to render ribbon
                    props.fireEvent('__onFormUpdated', { formContext });
                    props.fireEvent('__refreshRibbon', { formContext });
                    setTabs(_form.tabs);

                    //TODO: extract from portal
                    if (props.extraqs && props.extraqs.talxis_setfocustotab && _form.tabs.some(x => x.name === props.extraqs.talxis_setfocustotab)) {
                        const tab = _form.tabs.find(x => x.name === props.extraqs.talxis_setfocustotab);
                        setCurrentTab(tab);
                        currentTabIdRef.current = tab.id;
                    }
                    else {
                        const tab = _form.tabs.find(x => x.visible === true);
                        if (tab) {
                            setCurrentTab(tab);
                            currentTabIdRef.current = tab.id;
                        }
                    }
                    // Form is set as the last, because we trigger the onLoad event after the form is set.
                    setForm(_form);
                }
            }
            catch (err) {
                if (mounted) setError(err as any);
            }
        };

        if (props?.isNavbarLoaded) {
            initAsync();
        }

        return () => mounted = false;
    }, [entityId, props.formId, props.entityName, props.extraqs, props.isNavbarLoaded]);

    useEffect(() => {
        if (form) {
            const onChangeEvents = form.events.filter(x => x.name == "onchange");
            for (const onChangeEvent of onChangeEvents) {
                if (
                    (entityChanges[onChangeEvent.attribute] !== undefined && entityChanges[onChangeEvent.attribute] !== previousEntityChangesRef.current[onChangeEvent.attribute]) ||
                    (entityChanges[onChangeEvent.attribute] !== undefined && JSON.stringify(entityChanges[onChangeEvent.attribute]) !== JSON.stringify(previousEntityChangesRef.current[onChangeEvent.attribute]))
                ) {
                    for (const handler of onChangeEvent.handlers) {
                        // TODO: Maybe load the library dynamically here instead at FormMapper
                        const parameters: any[] = [];
                        if (handler.passExecutionContext) {
                            parameters.push(xrmExecutionContextRef.current);
                        }
                        if (handler.parameters) {
                            parameters.push(handler.parameters);
                        }

                        try {
                            console.log(`Executing ${handler.functionName} function on ${form.id} form onchange event.`);
                            // TODO: Pass other parameters as per handler.parameters
                            executeFunctionByName(handler.functionName, window, parameters);
                        }
                        catch (err) {
                            console.error(`Error in custom script: ${handler.functionName}`, err);
                        }
                    }
                }
            }
            previousEntityChangesRef.current = entityChanges;
        }
    }, [entityChanges]);

    useEffect(() => {
        // form property is only set once during initialization, so it shouldn't change during runtime, therefor we can consider this being `onload`
        if (form && onLoadExecutedRef.current === false) {
            const onLoadEvents = form.events.filter(x => x.name == "onload");
            for (const onLoadEvent of onLoadEvents) {
                for (const handler of onLoadEvent.handlers) {
                    // TODO: Maybe load the library dynamically here instead at FormMapper
                    const parameters: any[] = [];
                    if (handler.passExecutionContext) {
                        parameters.push(xrmExecutionContextRef.current);
                    }
                    if (handler.parameters) {
                        parameters.push(handler.parameters);
                    }

                    try {
                        console.log(`Executing ${handler.functionName} function on ${form.id} form onload event.`);
                        // TODO: Pass other parameters as per handler.parameters
                        executeFunctionByName(handler.functionName, window, parameters);
                    }
                    catch (err) {
                        console.error(`Error in custom script: ${handler.functionName}`, err);
                    }
                }
            }
            onLoadExecutedRef.current = true;
        }
        formRef.current = form;
    }, [form]);

    useEffect(() => {
        const formContext = xrmExecutionContextRef.current?.getFormContext();
        if (!formContext) return;
        props.fireEvent('__onFormUpdated', { formContext });
        if (formContext.data.getIsDirty()) {
            setFormUpdated(true);
            return;
        }
        setFormUpdated(false);
    }, [entityChanges]);

    useEffect(() => {
        if (shouldRefresh) {
            setShouldRefresh(false);
        }
    }, [shouldRefresh]);

    const refreshForm = async (): Promise<void> => {
        const entityDefinition = props.entityName ? await EntityDefinition.getAsync(props.entityName) : null;
        let expand: string = null;
        let partyListRelationshipName: string = null;
        // If entity contains partylist attributes
        if (entityDefinition?.IsActivity) {
            const participationTypeMasks = getParticipationTypeMasks(props.entityName); // get participation masks for entity
            partyListRelationshipName = entityDefinition.OneToManyRelationships.find(x => x.ReferencedEntity == props.entityName && x.ReferencingAttribute == "activityid")?.ReferencedEntityNavigationPropertyName;
            expand = `?$expand=${partyListRelationshipName}($filter=Microsoft.Dynamics.CRM.In(PropertyName='participationtypemask',PropertyValues=[${Object.values(participationTypeMasks).map(x => { return `'${x}'`; })}]))`;
        }
        const recordData = (props.entityName && entityId) ? await window.Xrm.WebApi.retrieveRecord(props.entityName, entityId, expand) : {};
        mapLookups(recordData);
        mapMultiSelectPicklists(entityDefinition, recordData);
        if (entityDefinition?.IsActivity) {
            await mapPartyListLookups(recordData, partyListRelationshipName);
        }
        // Update current recordData with extraQs passed from Page.
        for (const [property, value] of Object.entries(props.extraqs ?? [])) {
            // We are in a dialog, so we don't set anything related to lookup from extraQs
            if (!props.entityName) {
                recordData[property] = value;
            }
            else {
                const attribute = entityDefinition.Attributes.find(x => x.LogicalName === property);
                if (attribute?.AttributeType === "Lookup" || attribute?.AttributeType === "Owner" || attribute?.AttributeType === "Customer") {
                    const lookupValue: ComponentFramework.LookupValue = {
                        id: value as string,
                        entityType: attribute.EntityLogicalName
                    };
                    recordData[property] = [lookupValue];
                }
                else {
                    recordData[property] = value;
                }
            }
        }

        setEntity(recordData);
        entityRef.current = recordData;
        setEntityChanges({});
        entityChangesRef.current = {};
        setShouldRefresh(true);
    };

    const isFormValid = (): boolean => {
        setValidate(true);

        console.groupCollapsed("Form Validation", props.formId);

        let validationResult = true;

        const resultingEntity: ComponentFramework.WebApi.Entity = { ...entityRef.current, ...entityChangesRef.current };
        console.log("Entity", resultingEntity);
        for (const control of getAllControls(formRef.current.tabs)) {
            //check for valid field, control definiton is null or undefined in case of a Button
            if (control.definition?.manifest) {
                const isRequired = getRequiredLevel(attributeConfigurationRef.current[control.datafieldname], control.isRequired);
                const primaryBoundField = Object.entries(control.bindings).find(x => x[1].isStatic === false);

                const manifestDefinition = control.definition.manifest.properties.find(x => x.name == primaryBoundField?.[0]);
                const type = manifestDefinition?.ofType ?? DataType.SingleLineText;

                console.log(control.name, control.datafieldname, isRequired, type);

                if (!isFieldValid(isRequired, control.disabled, control.visible, type, resultingEntity, primaryBoundField[1].value)) {
                    console.log("Invalid value!", control.name, control.datafieldname, isRequired, type);
                    validationResult = false;
                    break;
                }
            }
        }

        console.groupEnd();

        return validationResult;
    };

    const saveForm = async (): Promise<Xrm.CreateResponse | void> => {
        //propagate save to child forms
        const savePromises: Promise<void>[] = [];
        for (const key of Object.keys(childFormContextsRef.current)) {
            //@ts-ignore - not implementing promise like
            savePromises.push(childFormContextsRef.current[key].data.save());
        }
        await Promise.all(savePromises);
        setFormUpdated(false);
        let isDefaultPrevented = false;
        const isFormValidResult = isFormValid();
        if (isFormValidResult) {
            for (const handler of onSaveRef.current) {
                console.log("Executing onSave handler", handler);
                try {
                    const onSaveContext: Xrm.Events.SaveEventContext = {
                        ...xrmExecutionContextRef.current,
                        getEventArgs: (): Xrm.Events.SaveEventArguments => {
                            return {
                                getSaveMode: () => {
                                    throw new Error("Not implemented!");
                                },
                                isDefaultPrevented: () => {
                                    return isDefaultPrevented;
                                },
                                preventDefault: () => {
                                    isDefaultPrevented = true;
                                    console.log("onSave: Prevent default called!");
                                },
                                preventDefaultOnError: () => {
                                    throw new Error("Not implemented!");
                                }
                            };
                        },
                    };
                    const result = handler.apply(window, [onSaveContext]);
                    if (result && isPromise(result)) {
                        await result;
                    }
                }
                catch (err) {
                    console.error("Error while executing custom onPostSave handler!", err);
                }
            }
        }
        if (isFormValidResult && !isDefaultPrevented) {
            var saveResponse = await saveUpdatedFields();
            if (saveResponse === false) {
                setFormUpdated(true);
                window.Xrm.Navigation.openErrorDialog({
                    message: getTranslation("failedToSave")
                });
            }
            else if (saveResponse !== undefined) {
                for (const handler of onPostSaveRef.current) {
                    console.log("Executing onPostSave handler", handler);
                    try {
                        handler.apply(window, [xrmExecutionContextRef.current]);
                    }
                    catch (err) {
                        console.error("Error while executing custom onPostSave handler!", err);
                    }
                }
                setEntity({ ...entityRef.current, ...entityChangesRef.current });
                entityRef.current = { ...entityRef.current, ...entityChangesRef.current };
                setEntityChanges({});
                entityChangesRef.current = {};
                if (typeof saveResponse === "object") {
                    // TODO: Update URL of the current open form unless the URL entityName is talxis_portalpage, use Xrm.Navigation.navigateTo
                    // This should also reflect in entityId because further changes will result in new records being created
                    const entityId = saveResponse.id;
                    const paramsData = queryString.parse(window.location.search.substring(1))?.data as string;
                    const data: QueryData = (paramsData) ? JSON.parse(paramsData) : null;
                    // We don't want to make any URL changes when present on talxis_portalpage or when in Quick Create dialog because it will get closed once record is created
                    // also dont make any URL changes while child forms are being saved
                    if (data.entityName !== 'talxis_portalpage' && props.isQuickCreate !== true && props.isMainTopLevel) {
                        data.entityId = entityId;
                        const url = `${window.location.pathname}?data=${encodeURIComponent(JSON.stringify(data))}`;
                        setEntityId(entityId);
                        history.replace(url);
                    }
                }
                // Only return the response if it is a create operation
                if (saveResponse !== true) {
                    return saveResponse;
                }
                // If it's just update operation, also return entity reference so we can safely close the dialog
                // TODO: This should be reworked to throw when error occurs or return true/false when update
                else {
                    return {
                        entityType: props.entityName,
                        id: entityId
                    };
                }
            }
        }
        else {
            console.log(`Skipped form saving, isFormValid: ${isFormValidResult}, isDefaultPrevented: ${isDefaultPrevented}`);
            if (!isFormValidResult) {
                Xrm.Navigation.openAlertDialog({
                    text: getTranslation("missingRequired"),
                    title: getTranslation("savingError"),
                    confirmButtonLabel: "OK"
                });
            }
            setFormUpdated(true);
        }
    };
    interface IPartyListObject {
        [key: string]: string | number;
    }
    const saveUpdatedFields = async (): Promise<boolean | Xrm.CreateResponse> => {
        try {
            const entityChanges = { ...entityChangesRef.current };
            const keys = Object.keys(entityChanges);
            const entityDefinition = await EntityDefinition.getAsync(props.entityName);
            for (const key of keys) {
                const attribute = entityDefinition.Attributes.find(x => x.LogicalName === key);
                if (attribute.AttributeType === "Lookup" || attribute.AttributeType === "Owner" || attribute.AttributeType === "Customer") {
                    if (entityChanges[key] !== null) {
                        let referencingAttribute = key;
                        const values: ComponentFramework.LookupValue[] = entityChanges[key];
                        if (values?.length > 0) {
                            if (values.length > 1) {
                                throw new Error("Multiple values passed to save for lookup, this is unsupported!");
                            }
                            const lookupLogicalName = values[0].entityType;
                            const lookupTargetEntityDefinition = await EntityDefinition.getAsync(lookupLogicalName);
                            // This is a workaround where Dataverse doesn't seem to return OneToManyRelationships properly - eg. contact and parentcustomerid, _contact returns in OneToMany whereas _account returns in ManyToOne (with OneToMany type)
                            referencingAttribute = entityDefinition.OneToManyRelationships.find(x => x.ReferencedEntity == lookupLogicalName && x.ReferencingAttribute == referencingAttribute)?.ReferencingEntityNavigationPropertyName ??
                                entityDefinition.ManyToOneRelationships.find(x => x.ReferencedEntity == lookupLogicalName && x.ReferencingAttribute == referencingAttribute)?.ReferencingEntityNavigationPropertyName;
                            // Fixes edge case around ownerid which doesn't map to standard fields ownerid_systemuser but owninguser
                            if (key === "ownerid") {
                                referencingAttribute = key;
                            }
                            entityChanges[`${referencingAttribute}@odata.bind`] = `/${lookupTargetEntityDefinition.EntitySetName}(${values[0].id})`;
                        }
                        else {
                            if (attribute.Targets?.length > 1) {
                                // Polymorphic lookups need to be referenced via _x_value
                                referencingAttribute = `_${referencingAttribute}_value`;
                            }
                            entityChanges[`${referencingAttribute}@odata.bind`] = null;
                        }
                        delete entityChanges[key];
                    }
                }
                if (attribute.AttributeType === "DateTime") {
                    if (entityChanges[key]) {
                        if (attribute.DateTimeBehavior.Value === "DateOnly") {
                            entityChanges[key] = entityChanges[key].toISOString().split('T')[0];
                        } else {
                            entityChanges[key] = entityChanges[key].toISOString();
                        }
                    }
                }
                if (attribute.AttributeType === "PartyList") {
                    const values: ComponentFramework.LookupValue[] = entityChanges[key];
                    const participationTypeMask = getParticipationTypeMasks(props.entityName, attribute.LogicalName);
                    const referencedRelationshipName = entityDefinition.OneToManyRelationships.find(x => x.ReferencedEntity == props.entityName && x.ReferencingAttribute == "activityid")?.ReferencedEntityNavigationPropertyName;
                    let partyListObject: IPartyListObject[] = [];
                    if (entityChanges[referencedRelationshipName]) {
                        partyListObject = [...(entityChanges[referencedRelationshipName].filter((x: IPartyListObject) => x["participationtypemask"] !== participationTypeMask))];
                    }
                    else {
                        partyListObject = [...await assemblePartyListObject(participationTypeMask as number)];
                    }
                    for (const value of values) {
                        const lookupLogicalName = value.entityType;
                        const lookupTargetEntityDefinition = await EntityDefinition.getAsync(lookupLogicalName);
                        const referencingAttribute: string = lookupTargetEntityDefinition.OneToManyRelationships.find(x => x.ReferencedEntity == lookupLogicalName && x.ReferencingAttribute == "partyid")?.ReferencingEntityNavigationPropertyName ??
                            entityDefinition.ManyToOneRelationships.find(x => x.ReferencedEntity == lookupLogicalName && x.ReferencingAttribute == "partyid")?.ReferencingEntityNavigationPropertyName;
                        partyListObject.push({
                            "participationtypemask": participationTypeMask as number,
                            [`${referencingAttribute}@odata.bind`]: `/${lookupTargetEntityDefinition.EntitySetName}(${value.id})`
                        });
                    }
                    entityChanges[referencedRelationshipName] = partyListObject;
                    delete entityChanges[key];
                }
                if (Array.isArray(entityChanges[key])) entityChanges[key] = entityChanges[key].join();
            }
            if (entityId) {
                const updateRequest: IUpdateRequest = {
                    etn: props.entityName,
                    id: entityId,
                    payload: entityChanges,
                    suppressDupeDetection: false,
                    getMetadata: function () { return { boundParameter: null, parameterTypes: {}, operationType: 2, operationName: "Update" }; }
                };

                try {
                    const response = await Xrm.WebApi.online.execute(updateRequest);
                    if (response.ok) return true;
                }
                catch (error) {
                    if (error.errorCode === WebApiErrors.DUPLICATE_ERROR) {
                        Xrm.Utility.closeProgressIndicator();
                        // @ts-ignore - openDialog is not part of Xrm types
                        const dialogResult = await Xrm.Navigation.openDialog("talxis_duplicatedetectiondialog", {
                            position: 1,
                            height: 500,
                            width: 500
                        }, {
                            talxis_entityname: props.entityName,
                            talxis_data: JSON.stringify(entityChanges),
                            talxis_recordidofrecordtobemerged: null
                        });
                        const mergedRecordId: string = dialogResult["parameters"]["talxis_mergedrecordid"];
                        if (mergedRecordId !== null) {
                            return {
                                entityType: props.entityName,
                                id: mergedRecordId ?? ""
                            };
                        }
                    }
                }
            }
            else {
                const createRequest: ICreateRequest = {
                    etn: props.entityName,
                    payload: entityChanges,
                    suppressDupeDetection: false,
                    getMetadata: function () { return { boundParameter: null, parameterTypes: {}, operationType: 2, operationName: "Create" }; }
                };

                try {
                    const response = await Xrm.WebApi.online.execute(createRequest);
                    if (response.ok) {
                        const entityId = response.headers.get("OData-EntityId").replace(/(^.*\(|\).*$)/g, '');

                        return {
                            entityType: props.entityName,
                            id: entityId
                        };
                    }
                } catch (error) {
                    if (error.errorCode === WebApiErrors.DUPLICATE_ERROR) {
                        Xrm.Utility.closeProgressIndicator();
                        // @ts-ignore - openDialog is not part of Xrm types
                        const dialogResult = await Xrm.Navigation.openDialog("talxis_duplicatedetectiondialog", {
                            position: 1,
                            height: 500,
                            width: 500
                        }, {
                            talxis_entityname: props.entityName,
                            talxis_data: JSON.stringify(entityChanges),
                            talxis_recordidofrecordtobemerged: null
                        });
                        const mergedRecordId: string = dialogResult["parameters"]["talxis_mergedrecordid"];
                        if (mergedRecordId !== null) {
                            return {
                                entityType: props.entityName,
                                id: mergedRecordId ?? ""
                            };
                        }
                    }
                }
            }
        }
        catch {
            return false;
        }
    };

    const changeTab = (selectedTab: IForm.Tab) => {
        setCurrentTab(selectedTab);
    };

    const getSectionBodyStyles = (section: IForm.Section) => {
        let className = 'TALXIS__form__section__body';
        return `${className} ${mergeStyles({
            gridTemplateColumns: `repeat(${section.numOfColumns}, minmax(0, 1fr))`,
        })}`;
    };

    const getColumnsStyles = (columns: IForm.Column[]) => {
        let className = 'TALXIS__form__columns';
        const fractions = columns.map(x => x.width).join(' ').replaceAll("%", "fr");
        return `${className} ${mergeStyles({
            gridTemplateColumns: fractions,
        })}`;
    };

    const getFormStyles = () => {
        return mergeStyles({
            ':global(.TALXIS__portal__content > main > div > div > div > div > .TALXIS__form)': {
                boxShadow: theme.semanticColors.cardShadow
            },
            '&, .TALXIS__form__section': {
                backgroundColor: theme.semanticColors.bodyBackground,
            }
        });
    };
    const getRibbonWrapperStyles = () => {
        return `${mergeStyles({
            display: 'flex',
            alignItems: 'center',
            marginTop: 15,
            paddingBottom: 1,
            '>div:first-child': {
                flexGrow: 1,
                height: 44,
                minWidth: 0
            },
            '>span': {
                fontWeight: 600,
                color: 'grey',
                display: 'block',
                textAlign: 'right'
            }
        })}`;
    };

    if (error) {
        // TODO: We should probably handle this more gracefully than just throwing errors around.
        throw error;
    }

    const provider = useMemo((): IFormContext => {
        const context = {
            entityId: entityId,
            entityName: props.entityName,
            entity: entity,
            entityChanges: entityChanges,
            formUniqueName: props.formUniqueName,
            xrmExecutionContext: xrmExecutionContextRef.current,
            setEntityChanges: _setEntityChanges,
            onRecordSelect: _onRecordSelect,
            onRecordsSelect: _onRecordsSelect,
            registerChildControl: _registerChildControl,
            isMobile: isMobile,
            validate: validate,
            attributeConfiguration: { ...attributeConfigurationRef.current }
        };
        props.fireEvent('__onFormProviderUpdated', context);
        return context;

    }, [entityId, props.entityName, entity, entityChanges, attributeConfiguration, form, validate]);

    return (
        <FormContext.Provider value={provider}>
            <section ref={ref} data-id={props.formId} className={`TALXIS__form ${getFormStyles()}`}>
                {(!form) &&
                    <Placeholder />
                }
                {form &&
                    <>
                        {ribbonEnabled() && ribbonCommandBarProps &&
                            <div className={getRibbonWrapperStyles()}>
                                <div>
                                    {<CommandBar {...ribbonCommandBarProps} />}
                                </div>
                                {formUpdated && <Text>{window.TALXIS.Portal.Translations.getLocalizedString('components/dialogs/NavigationPromptDialog/Title')}</Text>}
                            </div>
                        }
                        <div>
                            {((!currentTab || currentTab?.showLabel) || (isEditable && !props.isQuickCreate)) &&
                                <Tabs
                                    tabs={(!currentTab && tabs) || currentTab?.showLabel ? tabs : []}
                                    data-updated={formUpdated}
                                    currentTab={currentTab}
                                    onTabChange={changeTab}
                                />
                            }
                        </div>
                    </>
                }
                {currentTab && !shouldRefresh &&
                    <div>
                        <div className={getColumnsStyles(currentTab?.columns)}>
                            {currentTab?.columns.map(column =>
                                <div key={column.id} className='TALXIS__form__column'>
                                    {column.sections.filter((section => (isMobile && section.availableForPhone && section.visible) || (!isMobile && section.visible))).map(section =>
                                        <Card key={section.id} className='TALXIS__form__section' data-id={section.name}>
                                            {section.showLabel &&
                                                <Card.Section className='TALXIS__form__section__header'>
                                                    <Text variant='mediumPlus' data-id={`form-sectionHeader-${section.name}`}>{section.label}</Text>
                                                </Card.Section>
                                            }
                                            <Card.Section className={getSectionBodyStyles(section)}>
                                                {section.rows.map(row => row.cells.map((cell, i) => <Cell key={cell.id} cell={cell} onFormUpdated={(arg) => {
                                                    childFormContextsRef.current[cell.control.datafieldname] = arg.formContext;
                                                    props.fireEvent('__onFormUpdated', {
                                                        formContext: xrmExecutionContextRef.current.getFormContext()
                                                    });
                                                }} />))}
                                            </Card.Section>
                                        </Card>
                                    )}
                                </div>
                            )}
                        </div>
                        {props.isQuickCreate &&
                            <DialogFooter>
                                <PrimaryButton
                                    text={entityId ? getTranslation("saveAndClose") : getTranslation("create")}
                                    onClick={async () => {
                                        Xrm.Utility.showProgressIndicator(entityId ? getTranslation("savingChanges") : getTranslation("creatingRecord"));
                                        const result = await saveForm();
                                        Xrm.Utility.closeProgressIndicator();
                                        if (result) {
                                            props.fireEvent("onCreateCallback", {
                                                id: result.id,
                                                name: entityRef.current[entityDefinitionRef.current.PrimaryNameAttribute] ?? null,
                                                entityType: result.entityType
                                            });
                                        }
                                    }} />
                                <DefaultButton
                                    text={getTranslation("close")}
                                    onClick={() => {
                                        props.fireEvent("onCloseCallback", {});
                                    }} />);
                            </DialogFooter>
                        }
                    </div>
                }
            </section>
        </FormContext.Provider>
    );
};